import type { QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';

import {
  queryFilterOptions,
  type FilterDef,
  type FilterOption,
} from '@nucleus-commerce/admin-kit/index-table';
import type {
  InventoryLevel,
  Product,
  ProductSummary,
  StockLocation,
} from '@nucleus-commerce/api-client/admin';

import { productListQueryOptions, productQueryOptions } from '@/features/products';

import { stockLocationsQueryOptions } from '../hooks/use-stock-locations';

/** Fields GET /inventory sorts on; anything else is a 400. */
export const INVENTORY_SORTS = ['sku', 'available', 'onHand', 'updatedAt'] as const;

/**
 * The filters as the URL and the API spell them, for the route's search schema and loader.
 * `locationId` takes several ids; without it the API answers for the default location.
 */
export const INVENTORY_FILTER_FIELDS = [
  { key: 'locationId', type: 'async' },
  { key: 'lowStock', type: 'boolean' },
  { key: 'available', type: 'number-range' },
  { key: 'productId', type: 'async' },
] as const;

export interface InventoryFiltersOptions {
  /** The `inventory` namespace. */
  t: TFunction;
  queryClient: QueryClient;
  storeId: string;
}

/** Filters of the stock levels table, in the order the filter menu lists them. */
export function inventoryFilters({
  t,
  queryClient,
  storeId,
}: InventoryFiltersOptions): Array<FilterDef<InventoryLevel>> {
  const locationOption = (location: StockLocation): FilterOption => ({
    value: location.id,
    label: location.name,
    ...(location.isDefault ? { description: t('locations.default') } : {}),
  });
  const productOption = (product: Product | ProductSummary): FilterOption => ({
    value: product.id,
    label: product.title,
  });

  return [
    {
      key: 'locationId',
      label: t('filters.location'),
      type: 'async',
      pinned: true,
      aliases: ['location'],
      // A store has at most 20 locations: one request lists them all, the picker narrows by name.
      ...queryFilterOptions(queryClient, {
        search: () => stockLocationsQueryOptions(storeId),
        toOptions: (locations) => locations.map(locationOption),
        resolve: {
          query: () => stockLocationsQueryOptions(storeId),
          toOptions: (locations) => locations.map(locationOption),
        },
      }),
    },
    {
      key: 'lowStock',
      label: t('filters.lowStock'),
      type: 'boolean',
      pinned: true,
      trueLabel: t('filters.lowStockYes'),
      falseLabel: t('filters.lowStockNo'),
    },
    { key: 'available', label: t('filters.available'), type: 'number-range', step: 1 },
    {
      key: 'productId',
      label: t('filters.product'),
      type: 'async',
      // The endpoint takes one product.
      multiple: false,
      aliases: ['product'],
      ...queryFilterOptions(queryClient, {
        search: (q) => productListQueryOptions({ ...(q ? { q } : {}), limit: 20 }),
        toOptions: (page) => page.items.map(productOption),
        resolve: {
          query: ([id]) => productQueryOptions(id ?? ''),
          toOptions: (product) => [productOption(product)],
        },
      }),
    },
  ];
}
